import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import ModalStyled from '@components/modal/ModalStyled';
import { css } from '@emotion/react';
import { CircleStar } from '@lusha/icons';
import { useAnalytics, AnalyticsContextProvider } from '@lusha-engage/analytics';
import { Loader } from '@lusha-ui/core';
import { Sparkle2Icon, ExternalLinkIcon } from '@lusha-ui/icons';
import { Label } from '@lusha-ui/label';
import { Tab, Tabs } from '@lusha-ui/tabs';
import { Text, Link } from '@lusha-ui/text';
import { colors } from '@lusha-ui/theme';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { actions as popupsActions } from '@reducers/popups/reducer';
import { selectPlanByCode } from '@reducers/pricing/selectors';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PlanCard from './components/PlanCard';
import StarterPlanCard from './components/StarterPlanCard';
import { BILLING_CYCLES, DEFAULT_BILLING_CYCLE, DEFAULT_EXPANDED_PLAN, DYNAMIC_PLAN_CODES, HEADLINE_CONFIG, PLAN_CODES, } from './constants';
const createDefaultPlanSelections = () => DYNAMIC_PLAN_CODES.reduce((selections, planCode) => (Object.assign(Object.assign({}, selections), { [planCode]: { credits: 0, seats: 0 } })), {});
const UpgradePlanModal = () => {
    const analytics = useAnalytics();
    const dispatch = useAppDispatch();
    const { totalCredits, userPlanTotalCredits, pricing, isVisible, openSource } = useAppSelector(state => ({
        totalCredits: state.account.totalCredits,
        userPlanTotalCredits: state.account.userPlanTotalCredits,
        pricing: state.pricing,
        isVisible: state.popups.upgradePlan.isOpen,
        openSource: state.popups.upgradePlan.source,
    }));
    const [billingCycle, setBillingCycle] = useState(DEFAULT_BILLING_CYCLE);
    const [planSelections, setPlanSelections] = useState(createDefaultPlanSelections);
    const [expandedPlan, setExpandedPlan] = useState(DEFAULT_EXPANDED_PLAN);
    const isOutOfCreditsFlow = openSource === 'credit_exhaustion';
    const updatePlanSelection = useCallback((planCode, selection) => {
        setPlanSelections(current => (Object.assign(Object.assign({}, current), { [planCode]: Object.assign(Object.assign({}, current[planCode]), selection) })));
    }, []);
    const handleBillingCycleUpdate = (cycle) => {
        setBillingCycle(cycle);
        setPlanSelections(createDefaultPlanSelections());
        analytics.track({
            eventType: 'pricing_toggle_clicked',
            event_value: cycle,
            event_additional_info: {
                selected_plan: expandedPlan,
                current_credit_balance: totalCredits,
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: openSource,
            },
        });
    };
    const headlineConfig = useMemo(() => {
        if (totalCredits === 0) {
            return HEADLINE_CONFIG.outOfCredits;
        }
        if (totalCredits >= 1 && totalCredits <= 15) {
            return HEADLINE_CONFIG.almostOut;
        }
        return HEADLINE_CONFIG.healthy;
    }, [totalCredits]);
    const resetToDefaults = useCallback(() => {
        setBillingCycle(DEFAULT_BILLING_CYCLE);
        setPlanSelections(createDefaultPlanSelections());
        setExpandedPlan(DEFAULT_EXPANDED_PLAN);
    }, []);
    function handleClose() {
        dispatch(popupsActions.hideUpgradePlanPopup());
        resetToDefaults();
    }
    function handleCompareClick() {
        analytics.track({
            eventType: 'upgrade_compare_plans_clicked',
            event_additional_info: {
                selected_plan: expandedPlan,
                current_credit_balance: totalCredits,
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: openSource,
            },
        });
    }
    const handleExpandPlan = useCallback((planCode) => {
        if (expandedPlan === planCode) {
            return;
        }
        setExpandedPlan(planCode);
        analytics.track({
            eventType: 'plan_card_clicked',
            event_value: planCode,
            event_additional_info: {
                default_plan: DEFAULT_EXPANDED_PLAN,
                plugin_component: 'free_to_paid_upgrade_pop_up',
            },
        });
    }, [expandedPlan, analytics]);
    useEffect(() => {
        if (isVisible) {
            resetToDefaults();
            analytics.track({
                eventType: 'pricing_exposed',
                event_page_state_str: 'upgrade_pop_up',
                event_trigger_type: openSource,
                event_additional_info: {
                    plugin_click_source: openSource,
                    selected_plan: DEFAULT_EXPANDED_PLAN,
                    current_credit_balance: totalCredits,
                    plugin_component: 'free_to_paid_upgrade_pop_up',
                    billing_cycle: billingCycle,
                    header_state: totalCredits === 0 ? 'out_of_credits' : totalCredits > 15 ? 'healthy' : 'almost_out',
                },
            });
        }
    }, [isVisible]);
    useEffect(() => {
        if (!pricing.isLoaded || selectPlanByCode(pricing, expandedPlan))
            return;
        setExpandedPlan(PLAN_CODES.PRO);
    }, [pricing.isLoaded, expandedPlan]);
    useEffect(() => {
        if (!pricing.loadErrorMessage)
            return;
        analytics.track({
            eventType: 'pricing_error_displayed',
            event_value: 'plans_unavailable',
            event_additional_info: {
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: openSource,
                error_message: pricing.loadErrorMessage,
                current_credit_balance: totalCredits,
            },
        });
    }, [pricing.loadErrorMessage]);
    const scrollDefaultPlanIntoView = useCallback((plansList) => {
        const defaultPlanCard = plansList === null || plansList === void 0 ? void 0 : plansList.querySelector(`[data-plan-code="${DEFAULT_EXPANDED_PLAN}"]`);
        if (!plansList || !defaultPlanCard)
            return;
        plansList.scrollTop += defaultPlanCard.getBoundingClientRect().top - plansList.getBoundingClientRect().top;
    }, []);
    return (_jsx(ModalStyled, { isVisible: isVisible, onClose: handleClose, name: "upgrade-plan", wrapperPadding: 0, css: css `
        max-width: 340px !important;
      `, children: _jsx(AnalyticsContextProvider, { event_additional_info: {
                selected_plan: expandedPlan,
                current_credit_balance: totalCredits,
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: openSource,
            }, children: _jsxs("div", { css: css `
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - 76px);
            overflow: hidden;
            padding-left: 16px;
            padding-right: 16px;
            padding-bottom: 24px;

            @media only screen and (max-height: 600px) {
              padding-bottom: 12px;
            }
          `, children: [_jsxs("div", { css: css `
              display: flex;
              flex-direction: column;
              margin-bottom: 16px;

              @media only screen and (max-height: 600px) {
                margin-bottom: 8px;
              }
            `, children: [_jsx("div", { css: css `
                display: flex;
                align-items: center;
                margin-bottom: 4px;
              `, children: isOutOfCreditsFlow ? (_jsx(Text, { variant: "h3-b", children: "You\u2019ve run out of credits" })) : (_jsxs(_Fragment, { children: [_jsx(Sparkle2Icon, { css: css `
                      margin-right: 6px;
                    `, color: colors.lightScaleYellow, size: "normal" }), _jsx(Text, { variant: "h3-b", children: headlineConfig.headline })] })) }), _jsx(Text, { variant: "body-2-r", children: isOutOfCreditsFlow
                                    ? 'Upgrade your plan to get more credits and unlock advanced capabilities'
                                    : headlineConfig.subtext })] }), _jsxs("div", { css: css `
              padding: 8px;
              background: ${colors.lightGrey};
              border-radius: 4px;
              margin-bottom: 24px;

              @media only screen and (max-height: 600px) {
                margin-bottom: 8px;
              }
            `, children: [_jsx(CircleStar, { variant: "solid", color: "secondary" }), _jsxs(Text, { variant: "body-2-r", css: css `
                margin-left: 4px;
              `, children: ["Current plan: Free \u00B7 ", userPlanTotalCredits || 50, " credits/mo."] })] }), _jsx("div", { css: css `
              display: flex;
              justify-content: center;
            `, children: _jsxs(Tabs, { selectedTab: billingCycle, onChange: handleBillingCycleUpdate, size: "small", css: css `
                margin-bottom: 16px;

                @media only screen and (max-height: 600px) {
                  margin-bottom: 8px;
                }
              `, children: [_jsx(Tab, { value: BILLING_CYCLES.ANNUAL, active: billingCycle === BILLING_CYCLES.ANNUAL, label: "Annual", suffixElement: _jsx(Label, { css: css `
                      margin-left: 4px;
                      border-radius: 8px;
                      padding: 0 6px;
                      border: none;
                      margin-bottom: 5px;
                    `, size: "tiny", text: "Save 25%", backgroundColor: colors.fill.positive[1].hover, textColor: colors.text_and_icons.positive.DEFAULT, textVariant: "xTiny10B" }) }, `upgrade-options-tab-annual`), _jsx(Tab, { value: BILLING_CYCLES.MONTHLY, active: billingCycle === BILLING_CYCLES.MONTHLY, label: "Monthly" }, `upgrade-options-tab-monthly`)] }) }), pricing.isLoaded && (_jsxs("div", { ref: scrollDefaultPlanIntoView, css: css `
                display: flex;
                flex-direction: column;
                gap: 12px;
                overflow: auto;
                max-height: 100%;
                min-height: 0;
              `, children: [_jsx(StarterPlanCard, { billingCycle: billingCycle, isExpanded: expandedPlan === PLAN_CODES.STARTER, onExpand: () => handleExpandPlan(PLAN_CODES.STARTER), handleClose: handleClose }), DYNAMIC_PLAN_CODES.map(planCode => (_jsx(PlanCard, { planCode: planCode, selectedCredits: planSelections[planCode].credits, selectedSeats: planSelections[planCode].seats, billingCycle: billingCycle, isExpanded: expandedPlan === planCode, onExpand: () => handleExpandPlan(planCode), onCreditsChange: credits => updatePlanSelection(planCode, { credits }), onSeatsChange: seats => updatePlanSelection(planCode, { seats }), handleClose: handleClose }, planCode)))] })), _jsx("div", { css: css `
              display: flex;
              align-items: center;
              justify-content: center;
              padding-top: 12px;
            `, children: _jsxs(Text, { variant: "body-2-r", color: colors.text_and_icons.secondary.DEFAULT, children: ["Need more?", ' ', _jsxs(Link, { suffixElement: _jsx(ExternalLinkIcon, {}), target: "_blank", onClick: handleCompareClick, href: `${envConfig.DASHBOARD_URL}/pricing/?dashboard`, children: ["Compare all plans", ' '] })] }) }), pricing.isLoading && !pricing.isLoaded && (_jsx("div", { css: css `
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 300px;
              `, children: _jsx(Loader, { css: css `
                  margin-right: 8px;
                `, size: 50, backgroundColor: 'white' }) }))] }) }) }));
};
export default UpgradePlanModal;
